import { __ } from "@wordpress/i18n";
import {
	PanelBody,
	ButtonGroup,
	Button,
	SelectControl,
} from "@wordpress/components";
import { useState } from "@wordpress/element";
import {
	Icon,
	arrowRight,
	arrowLeft,
	arrowDown,
	arrowUp,
} from "@wordpress/icons";

const DEVICES = [
	{ label: __("Desktop", "swift-fse-blocks"), value: "Desktop" },
	{ label: __("Tablet", "swift-fse-blocks"), value: "Tablet" },
	{ label: __("Mobile", "swift-fse-blocks"), value: "Mobile" },
];

// Icon mappings for flex-direction
const FLEX_DIRECTION_OPTIONS = [
	{ label: __("Row", "swift-fse-blocks"), value: "row", icon: arrowRight },
	{
		label: __("Row Reverse", "swift-fse-blocks"),
		value: "row-reverse",
		icon: arrowLeft,
	},
	{ label: __("Column", "swift-fse-blocks"), value: "column", icon: arrowDown },
	{
		label: __("Column Reverse", "swift-fse-blocks"),
		value: "column-reverse",
		icon: arrowUp,
	},
];

const JUSTIFY_CONTENT_OPTIONS = [
	{ label: __("Flex Start", "swift-fse-blocks"), value: "flex-start" },
	{ label: __("Center", "swift-fse-blocks"), value: "center" },
	{ label: __("Flex End", "swift-fse-blocks"), value: "flex-end" },
	{ label: __("Space Between", "swift-fse-blocks"), value: "space-between" },
	{ label: __("Space Around", "swift-fse-blocks"), value: "space-around" },
	{ label: __("Space Evenly", "swift-fse-blocks"), value: "space-evenly" },
];

const ALIGN_ITEMS_OPTIONS = [
	{ label: __("Flex Start", "swift-fse-blocks"), value: "flex-start" },
	{ label: __("Center", "swift-fse-blocks"), value: "center" },
	{ label: __("Flex End", "swift-fse-blocks"), value: "flex-end" },
	{ label: __("Stretch", "swift-fse-blocks"), value: "stretch" },
	{ label: __("Baseline", "swift-fse-blocks"), value: "baseline" },
];

const ALIGN_CONTENT_OPTIONS = [
	{ label: __("Flex Start", "swift-fse-blocks"), value: "flex-start" },
	{ label: __("Center", "swift-fse-blocks"), value: "center" },
	{ label: __("Flex End", "swift-fse-blocks"), value: "flex-end" },
	{ label: __("Space Between", "swift-fse-blocks"), value: "space-between" },
	{ label: __("Space Around", "swift-fse-blocks"), value: "space-around" },
	{ label: __("Stretch", "swift-fse-blocks"), value: "stretch" },
];

const FLEX_WRAP_OPTIONS = [
	{ label: __("No Wrap", "swift-fse-blocks"), value: "nowrap" },
	{ label: __("Wrap", "swift-fse-blocks"), value: "wrap" },
	{ label: __("Wrap Reverse", "swift-fse-blocks"), value: "wrap-reverse" },
];

export default function ResponsiveFlexControl({ attributes, setAttributes }) {
	const [activeDevice, setActiveDevice] = useState("Desktop");

	const getAttributeName = (property) => {
		return `${property}${activeDevice}`;
	};

	const getValue = (property) => {
		return attributes[getAttributeName(property)] || "";
	};

	const setValue = (property, value) => {
		setAttributes({
			[getAttributeName(property)]: value,
		});
	};

	return (
		<PanelBody
			title={__("Flexbox Layout", "swift-fse-blocks")}
			initialOpen={false}
		>
			{/* Device Tabs */}
			<div style={{ marginBottom: "16px" }}>
				<ButtonGroup>
					{DEVICES.map((device) => (
						<Button
							key={device.value}
							variant={activeDevice === device.value ? "primary" : "secondary"}
							onClick={() => setActiveDevice(device.value)}
						>
							{device.label}
						</Button>
					))}
				</ButtonGroup>
			</div>

			{/* Flex Direction with Icons */}
			<div style={{ marginBottom: "16px" }}>
				<label
					style={{
						display: "block",
						marginBottom: "8px",
						fontWeight: 500,
					}}
				>
					{__("Flex Direction", "swift-fse-blocks")}
				</label>
				<ButtonGroup>
					{FLEX_DIRECTION_OPTIONS.map((option) => (
						<Button
							key={option.value}
							variant={
								getValue("flexDirection") === option.value
									? "primary"
									: "secondary"
							}
							onClick={() => setValue("flexDirection", option.value)}
							label={option.label}
							showTooltip={true}
						>
							<Icon icon={option.icon} />
						</Button>
					))}
				</ButtonGroup>
			</div>

			{/* Justify Content */}
			<SelectControl
				label={__("Justify Content", "swift-fse-blocks")}
				value={getValue("justifyContent")}
				options={[
					{ label: __("Select...", "swift-fse-blocks"), value: "" },
					...JUSTIFY_CONTENT_OPTIONS,
				]}
				onChange={(value) => setValue("justifyContent", value)}
			/>

			{/* Align Items */}
			<SelectControl
				label={__("Align Items", "swift-fse-blocks")}
				value={getValue("alignItems")}
				options={[
					{ label: __("Select...", "swift-fse-blocks"), value: "" },
					...ALIGN_ITEMS_OPTIONS,
				]}
				onChange={(value) => setValue("alignItems", value)}
			/>

			{/* Align Content */}
			<SelectControl
				label={__("Align Content", "swift-fse-blocks")}
				value={getValue("alignContent")}
				options={[
					{ label: __("Select...", "swift-fse-blocks"), value: "" },
					...ALIGN_CONTENT_OPTIONS,
				]}
				onChange={(value) => setValue("alignContent", value)}
			/>

			{/* Flex Wrap */}
			<SelectControl
				label={__("Flex Wrap", "swift-fse-blocks")}
				value={getValue("flexWrap")}
				options={[
					{ label: __("Select...", "swift-fse-blocks"), value: "" },
					...FLEX_WRAP_OPTIONS,
				]}
				onChange={(value) => setValue("flexWrap", value)}
			/>
		</PanelBody>
	);
}
